<template>
    <Primitive
        :as="as"
        :class="classes"
    >
        <Label
            v-if="rendersLabel"
            :id="labelId"
            :for="controlId"
            :class="[element('label'), { 'k-visually-hidden': hideLabel }]"
        >
            <slot name="label">{{ label }}</slot>
            <span
                v-if="required"
                :class="element('required')"
                aria-hidden="true"
            >*</span>
        </Label>

        <p
            v-if="hasDescription"
            :id="descriptionId"
            :class="element('description')"
        >
            <slot name="description">
                {{ description }}
            </slot>
        </p>

        <div :class="element('control')">
            <slot
                :id="controlId"
                :described-by="describedBy"
                :invalid="invalid"
                :required="required"
                :disabled="disabled"
                :size="size"
            />
        </div>

        <div
            :class="element('messages')"
            aria-live="polite"
        >
            <div
                v-if="hasErrorContent"
                :id="errorId"
                :class="element('error')"
            >
                <slot name="error">
                    <p
                        v-for="(message, index) in errorMessages"
                        :key="index"
                    >
                        {{ message }}
                    </p>
                </slot>
            </div>
        </div>
    </Primitive>
</template>

<script setup lang="ts">
import type { KFieldProps } from './types'
import useBem from '@yeeroen/use-bem'
import { Label, Primitive } from 'reka-ui'

import { computed, inject, provide, ref, useId, useSlots } from 'vue'
import { K_FIELDSET_CONTEXT } from '../fieldset/context'
import { K_FIELD_CONTEXT } from './context'

defineOptions({ name: 'KField' })

const props = withDefaults(defineProps<KFieldProps>(), {
    as: 'div',
    orientation: 'stacked',
    hideLabel: false,
    disabled: undefined,
    required: undefined,
})

defineSlots<{
    default?: (props: {
        id: string
        describedBy?: string
        invalid: boolean
        required: boolean
        disabled: boolean
        size: 'sm' | 'md' | 'lg'
    }) => unknown
    label?: () => unknown
    description?: () => unknown
    error?: () => unknown
}>()

const slots = useSlots()
const fieldset = inject(K_FIELDSET_CONTEXT, null)
const generatedId = useId()
const ownLabelCount = ref(0)
const { block, element, modifier } = useBem('k-field')

const controlId = computed(() => props.id ?? generatedId)
const descriptionId = computed(() => `${controlId.value}-description`)
const errorId = computed(() => `${controlId.value}-error`)
const hasLabel = computed(() => Boolean(slots.label || props.label))
const rendersLabel = computed(() => hasLabel.value && ownLabelCount.value === 0)
const labelId = computed(() => rendersLabel.value ? `${controlId.value}-label` : undefined)
const hasDescription = computed(() => Boolean(slots.description || props.description))
const errorMessages = computed(() => {
    if (typeof props.error === 'string') {
        return props.error ? [props.error] : []
    }

    return Array.isArray(props.error) ? props.error.filter(Boolean) : []
})
const invalid = computed(() => Boolean(props.error)
    && !(Array.isArray(props.error) && props.error.length === 0))
const hasErrorContent = computed(() => invalid.value
    && Boolean(slots.error || errorMessages.value.length))
const nativeDisabled = computed(() => fieldset?.nativeDisabled.value ?? false)
const disabled = computed(() => nativeDisabled.value
    || (props.disabled ?? fieldset?.disabled.value ?? false))
const required = computed(() => props.required ?? fieldset?.required.value ?? false)
const size = computed(() => props.size ?? fieldset?.size.value ?? 'md')
const describedBy = computed(() => [
    fieldset?.describedBy.value,
    hasDescription.value ? descriptionId.value : undefined,
    hasErrorContent.value ? errorId.value : undefined,
].filter(Boolean).join(' ') || undefined)
const classes = computed(() => block(
    modifier(props.orientation),
    modifier(size.value),
    modifier('invalid', invalid.value),
    modifier('disabled', disabled.value),
))

function registerOwnLabel() {
    ownLabelCount.value += 1
    let registered = true

    return () => {
        if (registered) {
            ownLabelCount.value -= 1
            registered = false
        }
    }
}

provide(K_FIELD_CONTEXT, {
    controlId,
    labelId,
    describedBy,
    invalid,
    required,
    disabled,
    nativeDisabled,
    size,
    registerOwnLabel,
})
</script>

<style lang="scss">
@use './k-field.scss';
</style>
